import { Recipe, StructuredIngredient } from "@/types/recipe";
import { DbRecipe } from "@/types/database";
import { Json } from "@/integrations/supabase/types";

/**
 * Convert a Recipe object to database format
 */
export const formatRecipeForDb = (recipe: Recipe, userId: string) => {
  return {
    id: recipe.id,
    title: recipe.title,
    description: recipe.description,
    ingredients: recipe.ingredients || [],
    instructions: recipe.instructions || [],
    prep_time: recipe.prepTime,
    cook_time: recipe.cookTime,
    total_time: recipe.totalTime,
    servings: recipe.servings,
    source_url: recipe.source_url || recipe.sourceUrl,
    image_url: recipe.image_url || recipe.imageUrl,
    storage_image_url: recipe.storage_image_url || null,
    user_image_url: recipe.user_image_url || null,
    storage_image_path: recipe.storage_image_path || null,
    user_image_path: recipe.user_image_path || null,
    imperial_version: recipe.imperial_version as unknown as Json,
    metric_version: recipe.metric_version as unknown as Json,
    tags: recipe.tags,
    user_id: userId,
    structured_ingredients_metric: recipe.structured_ingredients_metric as unknown as Json,
    structured_ingredients_imperial: recipe.structured_ingredients_imperial as unknown as Json,
    base_measurement_system: recipe.baseMeasurementSystem,
    is_quick_recipe: recipe.isQuickRecipe || false,
    quick_ingredients: recipe.quickIngredients as unknown as Json,
    suggestion_id: recipe.suggestion_id
  };
};

/**
 * Convert database recipe to Recipe object
 */
export const mapDbRecipeToRecipe = (dbRecipe: DbRecipe): Recipe => {
  return {
    id: dbRecipe.id,
    title: dbRecipe.title,
    description: dbRecipe.description,
    ingredients: dbRecipe.ingredients || [],
    instructions: dbRecipe.instructions || [],
    prepTime: dbRecipe.prep_time,
    cookTime: dbRecipe.cook_time,
    totalTime: dbRecipe.total_time,
    servings: dbRecipe.servings,
    source_url: dbRecipe.source_url,
    sourceUrl: dbRecipe.source_url,
    image_url: dbRecipe.image_url,
    storage_image_url: dbRecipe.storage_image_url || null,
    user_image_url: dbRecipe.user_image_url || null,
    storage_image_path: dbRecipe.storage_image_path || null,
    user_image_path: dbRecipe.user_image_path || null,
    imageUrl: dbRecipe.image_url,
    createdAt: dbRecipe.created_at,
    updatedAt: dbRecipe.updated_at || dbRecipe.created_at,
    tags: dbRecipe.tags,
    user_id: dbRecipe.user_id,
    imperial_version: dbRecipe.imperial_version as unknown as Recipe["imperial_version"],
    metric_version: dbRecipe.metric_version as unknown as Recipe["metric_version"],
    structured_ingredients_metric: dbRecipe.structured_ingredients_metric as unknown as StructuredIngredient[] | null,
    structured_ingredients_imperial: dbRecipe.structured_ingredients_imperial as unknown as StructuredIngredient[] | null,
    structured_ingredients: dbRecipe.structured_ingredients_metric as unknown as StructuredIngredient[] | null,
    baseMeasurementSystem: dbRecipe.base_measurement_system,
    isQuickRecipe: dbRecipe.is_quick_recipe || false,
    quickIngredients: dbRecipe.quick_ingredients as unknown as StructuredIngredient[] | null,
    suggestion_id: dbRecipe.suggestion_id
  };
};

/**
 * Convert partial Recipe updates to database format without nulling fields that
 * were not part of the update.
 */
export const formatRecipeUpdatesForDb = (updates: Partial<Recipe>) => {
  const dbUpdates: Record<string, unknown> = {};

  const assign = (key: keyof Recipe, dbKey: string, value = updates[key]) => {
    if (key in updates) {
      dbUpdates[dbKey] = value ?? null;
    }
  };

  assign("id", "id");
  assign("title", "title");
  assign("description", "description");
  assign("ingredients", "ingredients", updates.ingredients || []);
  assign("instructions", "instructions", updates.instructions || []);
  assign("prepTime", "prep_time");
  assign("cookTime", "cook_time");
  assign("totalTime", "total_time");
  assign("servings", "servings");
  assign("source_url", "source_url", updates.source_url || updates.sourceUrl || null);
  assign("sourceUrl", "source_url");
  assign("image_url", "image_url", updates.image_url || updates.imageUrl || null);
  assign("imageUrl", "image_url");
  assign("storage_image_url", "storage_image_url");
  assign("user_image_url", "user_image_url");
  assign("storage_image_path", "storage_image_path");
  assign("user_image_path", "user_image_path");
  assign("imperial_version", "imperial_version", updates.imperial_version as unknown as Json);
  assign("metric_version", "metric_version", updates.metric_version as unknown as Json);
  assign("tags", "tags");
  assign("structured_ingredients_metric", "structured_ingredients_metric", updates.structured_ingredients_metric as unknown as Json);
  assign("structured_ingredients_imperial", "structured_ingredients_imperial", updates.structured_ingredients_imperial as unknown as Json);
  assign("baseMeasurementSystem", "base_measurement_system");
  assign("isQuickRecipe", "is_quick_recipe", updates.isQuickRecipe || false);
  assign("quickIngredients", "quick_ingredients", updates.quickIngredients as unknown as Json);
  assign("suggestion_id", "suggestion_id");

  if ("updatedAt" in updates) {
    dbUpdates.updated_at = updates.updatedAt;
  }

  return dbUpdates;
};
